<template>
    <div style="width: 100%" class="invoices">
        <spinner v-if="loading" size="200"/>
        <div v-else>
            <h1>Vos factures</h1>
            <div v-if="lastInvoice" class="last-invoices">
                <h2>Votre dernière facture</h2>
                <div class="last-invoice">
                    <div>
                        <div>
                            <p v-if="isMultiMonth(lastInvoice)">
                                <span class="label">Période : </span>
                                <span class="capitalize">{{ lastInvoice.date.start | moment("MMMM") }}</span> -
                                <span class="capitalize">{{ lastInvoice.date.end | moment("MMMM") }}</span>
                                {{ lastInvoice.date.end | moment("YYYY") }}
                            </p>
                            <p v-else>
                                <span class="label">Mois : </span>
                                <span class="capitalize">{{ lastInvoice.date.start | moment("MMMM YYYY") }}</span>
                            </p>
                        </div>
                        <div>
                            <span class="label">Montant : </span>
                            <span v-if="!lastInvoice.withVAT">{{ lastInvoice.amountWithoutTaxes }}€ HT</span>
                            <span v-else>{{ lastInvoice.amount }}€ TTC</span>
                        </div>
                        <div class="flex">
                            <span class="label pr-1">Statut :</span>
                            <div class="roundLine">
                                <div :style="'background: ' + getColor(lastInvoice)" class="round"/>
                                <p>{{ lastInvoice.payed ? "Payée" : "Non payée" }}</p>
                            </div>
                        </div>
                    </div>
                    <a :href="'https://my.delicorner.co/UserBundle/uploads/Facture/document/' +lastInvoice.document"
                       class="lastInvoiceIcon" target="_blank">
                        <v-document class="last-invoice-doc" :text="lastInvoice.name"/>
                    </a>
                </div>
            </div>

            <div class="flex items-center justify-between w-full mb-4">
                <h2 class="mt-0 mb-0">Facturation trimestrielle</h2>
                <div class="flex items-center justify-end">
                    <div class="relative" @click="openModal">
                        <div
                            :class="{
                            'bg-blue': isQuarterlyInvoiceActive(),
                            'bg-grey': !isQuarterlyInvoiceActive(),
                            }"
                            class="w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out"
                        ></div>
                        <div
                            :class="{
                            'translate-x-6': isQuarterlyInvoiceActive(),
                            'translate-x-0': !isQuarterlyInvoiceActive(),
                            }"
                            class="absolute top-0 left-0 w-6 h-6 bg-white rounded-full shadow transform transition-transform duration-200 ease-in-out cursor-pointer"
                        ></div>
                    </div>
                </div>
            </div>

            <div>
                <span>Simplifiez votre gestion et faites des économies dès maintenant !</span>
                <div class="mt-4">Passez à la facturation trimestrielle et bénéficiez d'avantages :</div>

                <ul class="list-disc list-inside mt-1">
                    <li>Moins de factures à gérer : une seule facture par trimestre au lieu de trois.</li>
                    <li>Des frais de service réduits : plus le montant de votre facture est élevé, plus le pourcentage de frais diminue.</li>
                </ul>
                <div class="italic mt-4">
                    <span v-if="quarterlyInvoice.quarterlyBillingActivationDate !== null && quarterlyInvoice.quarterlyBillingCancellationRequestedDate === null">
                        En vigueur depuis le <strong>{{ formatDate(quarterlyInvoice.quarterlyBillingActivationDate) }}</strong>
                    </span>

                    <span v-else-if="quarterlyInvoice.quarterlyBillingRequestedDate !== null">
                        En vigueur à partir du <strong>{{ formatDate(quarterlyInvoice.nextQuarterDate) }}</strong>
                    </span>
                    <span v-else-if="quarterlyInvoice.quarterlyBillingCancellationRequestedDate !== null && quarterlyInvoice.quarterlyBillingActivationDate !== null">
                        Prendra fin à compter du <strong>{{ formatDate(quarterlyInvoice.nextQuarterDate) }}</strong>
                    </span>
                </div>
            </div>

            <h2>Prélèvement automatique</h2>
            <div v-if="gocardless !== undefined && !gocardless.hasActiveMandate">
                <p class="mb-0" >
                    Pour passer en prélèvement automatique:
                    <b-button @click.native="clickGoCardless">c'est par ici !</b-button>
                </p>
                <div class="flex align-center">
                    <p class="mb-0">
                        Posez-nous toutes vos questions à ce sujet directement sur le chat !
                    </p>
                </div>
            </div>
            <div v-else>
                <p class="mb-0">
                    Vous êtes actuellement en prélèvement automatique !
                </p>
                <div class="flex align-center">
                    <p class="mb-0">
                        <span class="label">Pour modifier vos coordonnées bancaires, </span>rendez-vous sur le chat !
                    </p>
                </div>
            </div>

            <div>
                <h2 class="mr-8">Vos factures antérieures</h2>
                <div @click="downloadAll">
                    <b-button>Télécharger toutes les factures en PDF</b-button>
                </div>
            </div>
            <p class="mb-3 mt-3">
                Retrouvez ici vos 12 anciennes factures. <br/>
                Contactez nous si vous souhaitez des factures antérieures !
            </p>
            <br>

            <div class="flex flex-wrap year-invoices">
                <a v-for="(el, i) of invoices" :key="i"
                   :href="'https://my.delicorner.co/UserBundle/uploads/Facture/document/' +el.document"
                   class="invoice mx-0 mb-6 pr-1" target="_blank">
                    <v-document :text="el.name"/>
                    <div :style="'background: ' + getColor(el)" class="round mt-2 mx-auto"/>
                </a>
            </div>
        </div>

        <div
            v-if="isModalOpen"
            class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
            id="modal-quarterly"
            >
            <div class="bg-white rounded-lg shadow-lg w-1/3">
                <div class="text-right px-4 py-2">
                    <button
                        @click="closeModal"
                        class="text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                        ✖
                    </button>
                </div>

                <div class="p-4 text-base text-center">
                    <p>
                        ℹ️
                    </p>
                    <p v-if="isQuarterlyInvoiceActive()">
                        Êtes-vous sûr de vouloir <strong>désactiver</strong> la facturation trimestrielle ?
                    </p>
                    <p v-else>
                        Êtes-vous sûr de vouloir <strong>activer</strong> la facturation trimestrielle ?
                    </p>
                    <p class="mt-4" v-if="quarterlyInvoice.quarterlyBillingActivationDate === null &&  quarterlyInvoice.quarterlyBillingRequestedDate === null">
                        La facturation trimestrielle entrera en vigueur à partir du <strong>{{ formatDate(quarterlyInvoice.nextQuarterDate) }}</strong>.<br>
                        Vous pouvez désactiver la facturation trimestrielle à tout moment. <br>
                        Elle prendra fin à la clôture du trimestre en cours.
                    </p>
                    <p class="mt-4" v-else-if="quarterlyInvoice.quarterlyBillingActivationDate === null && quarterlyInvoice.quarterlyBillingRequestedDate !== null">
                        La facturation trimestrielle sera désactivée immédiatement car elle n'est pas encore en vigueur.
                    </p>
                    <p class="mt-4" v-else-if="quarterlyInvoice.quarterlyBillingActivationDate !== null && quarterlyInvoice.quarterlyBillingCancellationRequestedDate !== null">
                        La facturation trimestrielle restera en vigueur car elle n'a pas encore pris fin.
                    </p>
                    <p class="mt-4" v-else>
                        La facturation trimestrielle prendra fin à compter du <strong>{{ formatDate(quarterlyInvoice.nextQuarterDate) }}</strong>
                    </p>
                </div>

                <div class="flex justify-center px-4 py-2">
                    <button
                        id="no-button"
                        @click="closeModal"
                        class="no-button hover:bg-red hover:text-white px-4 py-2 border border-red text-red rounded  transition duration-300"
                    >
                        Non
                    </button>
                    <button
                        id="yes-button"
                        @click="confirmAction"
                        class="yes-button ml-2 px-4 py-2 bg-main text-white border rounded transition duration-300"
                    >
                        Oui
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {apiCallRebuild, notifyErrorFetchRaw} from "@/helper";
import {api, API_ROUTES} from "@/Services/api.common.service";

export default {
    data() {
        return {
            axios: undefined,
            invoices: [],
            gocardless: undefined,
            loading: true,
            lastInvoice: {},
            isModalOpen: false,
            quarterlyInvoice: {
                quarterlyBillingActivationDate: null,
                quarterlyBillingCancellationRequestedDate: null,
                quarterlyBillingRequestedDate: null,
            }
        };
    },
    watch: {$route: "fetchData"},
    created() {
        this.fetchData();
    },
    methods: {
        clickGoCardless() {
          api().get(API_ROUTES.gocardless.mandate)
              .then(response => window.open(response.data.url, '_blank').focus())
              .catch(error => console.error(error));
        },
        getColor(invoice) {
            return invoice.payed ? "#6AA84F" : "#ED3833";
        },
        isMultiMonth(invoice) {
            return invoice.date.start.getMonth() !== invoice.date.end.getMonth();
        },

        downloadAll() {
            api(undefined, "blob")
                .get("account/invoices/download")
                .then(response => {
                    const fileURL = window.URL.createObjectURL(new Blob([response.data]));
                    const fileLink = document.createElement("a");

                    fileLink.href = fileURL;
                    fileLink.setAttribute("download", "Factures.zip");
                    document.body.appendChild(fileLink);

                    fileLink.click();
                })
                .catch(console.error);
        },
        async fetchData() {
            const notifyError = notifyErrorFetchRaw.bind(this);
            try {
                const apiRes = await apiCallRebuild(API_ROUTES.invoices);
                let invoices = apiRes?.invoices?.invoices || [];
                this.gocardless = apiRes?.gocardless;
                let quarterlyInvoice = apiRes?.quarterlyInvoice;

                quarterlyInvoice.nextQuarterDate = new Date(quarterlyInvoice.nextQuarterDate);
                quarterlyInvoice.quarterlyBillingActivationDate = null !== quarterlyInvoice.quarterlyBillingActivationDate ? new Date(quarterlyInvoice.quarterlyBillingActivationDate) : null;
                quarterlyInvoice.quarterlyBillingRequestedDate = null !== quarterlyInvoice.quarterlyBillingRequestedDate ? new Date(quarterlyInvoice.quarterlyBillingRequestedDate) : null;
                quarterlyInvoice.quarterlyBillingCancellationRequestedDate = null !== quarterlyInvoice.quarterlyBillingCancellationRequestedDate ? new Date(quarterlyInvoice.quarterlyBillingCancellationRequestedDate) : null;

                this.quarterlyInvoice = quarterlyInvoice;

                let yearAgo = new Date();
                yearAgo = new Date(
                    yearAgo.getFullYear() - 1,
                    yearAgo.getMonth() - 1,
                    1
                );
                yearAgo = yearAgo.getTime();
                invoices = invoices.filter(
                    invoice => new Date(invoice.date.start).getTime() >= yearAgo
                );

                const withDateObject = invoices
                    .map(invoice => ({
                        ...invoice,
                        date: {
                            start: new Date(invoice.date.start),
                            end: new Date(invoice.date.end)
                        }
                    }))
                    .sort((a, b) => b.date.start - a.date.start);

                this.lastInvoice = withDateObject[0];
                withDateObject.shift();
                this.invoices = withDateObject;

                this.loading = false;
            } catch (e) {
                console.error(e);
                notifyError();
            }
        },
        openModal() {
            this.isModalOpen = true;
        },
        closeModal() {
            this.isModalOpen = false;
        },
        async confirmAction() {
            try {
                if (this.quarterlyInvoice.quarterlyBillingActivationDate === null && this.quarterlyInvoice.quarterlyBillingRequestedDate === null) {
                    await api().patch(API_ROUTES.invoices.quarterlyInvoice, {
                        quarterlyBillingRequestedDate: new Date,
                    });

                    this.quarterlyInvoice.quarterlyBillingRequestedDate = new Date;
                } else if (this.quarterlyInvoice.quarterlyBillingActivationDate === null && this.quarterlyInvoice.quarterlyBillingRequestedDate !== null) {
                    await api().patch(API_ROUTES.invoices.quarterlyInvoice, {
                        quarterlyBillingCancellationRequestedDate: new Date,
                    });

                    this.quarterlyInvoice.quarterlyBillingRequestedDate = null;
                } else if (this.quarterlyInvoice.quarterlyBillingActivationDate !== null && this.quarterlyInvoice.quarterlyBillingCancellationRequestedDate === null) {
                    await api().patch(API_ROUTES.invoices.quarterlyInvoice, {
                        quarterlyBillingCancellationRequestedDate: new Date,
                    });

                    this.quarterlyInvoice.quarterlyBillingCancellationRequestedDate = new Date;
                } else if (this.quarterlyInvoice.quarterlyBillingActivationDate !== null && this.quarterlyInvoice.quarterlyBillingCancellationRequestedDate !== null) {
                    await api().patch(API_ROUTES.invoices.quarterlyInvoice, {
                        quarterlyBillingRequestedDate: new Date,
                    });

                    this.quarterlyInvoice.quarterlyBillingCancellationRequestedDate = null;
                }
            } catch (e) {
                console.error(e)
            };

            this.closeModal();
        },
        formatDate(date) {
            if (undefined === date || !date instanceof Date) {
                return null;
            }

            return date.toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: '2-digit',
            });
        },
        isQuarterlyInvoiceActive() {
            return (this.quarterlyInvoice.quarterlyBillingActivationDate !== null && this.quarterlyInvoice.quarterlyBillingCancellationRequestedDate === null) ||
                (this.quarterlyInvoice.quarterlyBillingActivationDate === null && this.quarterlyInvoice.quarterlyBillingRequestedDate !== null);
        }
    }
};
</script>

<style lang="scss" scoped>
.invoices {
    h1 {
        font-size: 3.5em;
        margin-bottom: 80px;
    }

    h2 {
        font-size: 1.7em;
        margin-top: 4rem;
        margin-bottom: 20px;
    }

    .capitalize {
        text-transform: capitalize;
    }

    .last-invoices h2 {
        margin-top: 30px;
    }

    button {
        font-weight: bold;
    }

    .label {
        font-weight: bold;
    }

    .round {
        border-radius: 100%;
        height: 15px;
        width: 15px;
        flex: 0 0 15px;
    }

    .roundLine {
        align-items: center;
        display: flex;

        div {
            margin-right: 8px;
        }
    }

    .last-invoice {
        display: flex;
        margin-bottom: 1.5rem;
        align-items: center;

        a {
            margin-left: 2rem;
        }
    }

    .year-invoices {
        a {
            display: flex;
            justify-content: space-between;
            flex-direction: column;
            width: 5%;
            height: 85px;
        }
    }

    #modal-quarterly p, #modal-quarterly p > strong {
        font-size: 16px;
        color: gray;
    }

    #no-button {
        border: 1px solid #e3342f !important;
    }

    #yes-button:hover {
        background-color: white !important;
        color: #05A895 !important;
        border: 1px solid #05A895;
    }
}
</style>
